/*
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */

import React, {useEffect, useRef, useState} from 'react';
import {Col, FormControl, FormGroup, FormLabel, Row} from 'react-bootstrap';
import {ConfirmationDialog} from '../Common/ConfirmationDialog';
import {service as UserAPIKeyService} from '../../Services/UserAPIKeyService';
import {KeyboardUtils} from '../../Utils';
import {SB_ENTER_KEY_CODE} from '../../Constants';

const STEP_NAME = 'name';
const STEP_TOKEN = 'token';

export const GenerateApiKeyModal = (props) => {
    const {
        show,
        onHide = () => {},
        onGenerated = (key) => {},
    } = props;

    const [step, setStep] = useState(STEP_NAME);
    const [name, setName] = useState('');
    const [token, setToken] = useState(null);
    const [copied, setCopied] = useState(false);
    const [error, setError] = useState(null);
    const nameInputRef = useRef(null);

    useEffect(() => {
        if (show) {
            setStep(STEP_NAME);
            setName('');
            setToken(null);
            setCopied(false);
            setError(null);
            // Delay to let the modal finish its enter animation before focusing
            setTimeout(() => nameInputRef.current?.focus(), 100);
        }
    }, [show]);

    const generate = async () => {
        setError(null);
        try {
            const response = await UserAPIKeyService.CreateApiKey(name);
            if (response?.result && response?.token) {
                setToken(response.token);
                setStep(STEP_TOKEN);
                onGenerated(response.result);
            } else {
                throw new Error(response?.message ?? 'An error occurred generating the API key.');
            }
        } catch (e) {
            setError(e?.message ?? 'An error occurred, please try again.');
        }
    };

    const copyToken = () => {
        navigator.clipboard.writeText(token).then(() => setCopied(true));
    };

    const isNameStep = step === STEP_NAME;
    const isFormValid = !!name.trim();

    const handleKeyPress = (event) => KeyboardUtils.handleKeyPress(SB_ENTER_KEY_CODE, event, () => { if (isFormValid) { void generate(); } });

    const title = isNameStep ? 'Generate API Key' : 'API Key Generated';

    const body = [
        error &&
        <Row key="error-row">
            <Col xs={12}>
                <p className="sb-model-viewer-validation-failure-text">{error}</p>
            </Col>
        </Row>,

        isNameStep &&
        <Row key="name-row">
            <Col xs={12}>
                <FormGroup>
                    <FormLabel>Key Name</FormLabel>
                    <FormControl
                        ref={nameInputRef}
                        type="text"
                        placeholder="e.g. Irrigation Incentives Production"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onKeyDown={handleKeyPress}
                        maxLength={255}
                    />
                    <small className="form-text text-muted">
                        Give this key a name so you can identify it later.
                    </small>
                </FormGroup>
            </Col>
        </Row>,

        !isNameStep &&
        <Row key="token-row">
            <Col xs={12}>
                <FormGroup>
                    <FormLabel>API Key</FormLabel>
                    <pre className="sb-api-key-token">{token ?? ''}</pre>
                </FormGroup>
                <p className="sb-model-viewer-validation-failure-text">
                    Copy this key now. You will not be able to see it again.
                </p>
            </Col>
        </Row>,
    ];

    return <ConfirmationDialog
        size="lg"
        Title={title}
        Body={body}
        Display={show}
        OnHide={isNameStep ? onHide : null}
        OnClose={onHide}
        CancelButtonMessage={isNameStep ? 'Cancel' : 'Dismiss'}
        OnConfirm={isNameStep ? (() => void generate()) : copyToken}
        ConfirmationButtonMessage={isNameStep ? 'Generate' : (copied ? 'Copied!' : 'Copy to Clipboard')}
        DisableConfirmationButton={isNameStep && !isFormValid}
    />;
};
